import React, { useCallback, useEffect, useMemo } from 'react';
import { Switch, Redirect } from 'react-router-dom';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useAsync } from 'react-async-hook';
import { useHistory } from 'react-router-dom';

import Layout from 'components/Layout';
import LoadingPage from 'components/LoadingPage';
import FamilyApplications from './FamilyApplications';
import ProtectedRoute from 'components/ProtectedRoute';
import { getFullName, SnapshotContext } from 'utils';
import styles from './Family.modules.less';
import { familyService } from 'services';
import { actions, selectors } from 'features/family';
import { ChevronDown, ListChecked, Logout, User, Document } from '@carbon/icons-react';
import { Button, Dropdown } from 'antd';
import { logout, selectUser } from 'features/auth';
import MyAccount from 'containers/MyAccount/MyAccount';
import { AddApplication2 } from './AddApplication2/AddApplication2';
import { applicationsAtom, applicationAtom, checklistItemsAtom } from './AddApplication2/atoms';
import { useAtom } from 'jotai';
import useHasRole from 'hooks/useHasRole';
import { ROLES } from 'constants';
import { useFlags } from 'launchdarkly-react-client-sdk';
import NewApplication from './NewApplication/NewApplication';
import legacyService from 'services/legacy.service';
import { Badge } from 'antd';

export default function Family(props) {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id: householdId } = useSelector(selectors.selectHousehold);
  const isFamily = useHasRole(ROLES.FAMILY);
  const currentUser = useSelector(selectUser);
  const { showFamilyApplicationChecklist } = useFlags();

  const { result: houseHold } = useAsync(async () => {
    const houseHold = await familyService.getHouseHold();
    const children = await familyService.getChildren(houseHold.id);
    const adults = await familyService.getAdults(houseHold.id);
    dispatch(actions.setHousehold());
    return { ...houseHold, children, adults };
  }, []);

  useEffect(() => {
    if (houseHold?.id) dispatch(actions.setHousehold(houseHold));
  }, [dispatch, houseHold, houseHold?.id]);

  // Use Jotai atom for applications (shared state that persists across route changes)
  const [applications, setApplications] = useAtom(applicationsAtom);
  const [, setApplication] = useAtom(applicationAtom);
  const [checklistItems, setChecklistItems] = useAtom(checklistItemsAtom);

  // Clear atoms when component unmounts (user navigates away from Family section)
  useEffect(() => {
    return () => {
      setApplications(null);
      setApplication(null);
      setChecklistItems(null);
    };
  }, [setApplications, setApplication, setChecklistItems]);

  // Fetch all applications for the family user - only fetch once using atom state
  useEffect(() => {
    const fetchApplications = async () => {
      // Only fetch if we haven't fetched before (applications is null)
      if (isFamily && currentUser?.id && applications === null) {
        try {
          const apps = await familyService.getApplicationsByUserId(currentUser.id);
          setApplications(apps);
        } catch (e) {
          console.error('Error fetching applications:', e);
          setApplications([]);
        }
      }
    };

    fetchApplications();
  }, [isFamily, currentUser?.id, applications, setApplications]);

  // Find the most recent submitted application
  const submittedApplication = useMemo(() => {
    if (!applications || applications.length === 0) return null;

    const submitted = applications
      .filter((app) => app.submittedDate)
      .sort((a, b) => new Date(b.submittedDate) - new Date(a.submittedDate));

    return submitted[0] || null;
  }, [applications]);

  // Fetch checklist items for the submitted application
  useEffect(() => {
    const fetchChecklistItems = async () => {
      if (!submittedApplication?.id || checklistItems !== null) return;

      try {
        const data = await legacyService.getChecklistItems(submittedApplication.id);
        setChecklistItems(data || []);
      } catch (error) {
        console.error('Error fetching checklist items:', error);
        setChecklistItems([]);
      }
    };

    fetchChecklistItems();
  }, [submittedApplication?.id, checklistItems, setChecklistItems]);

  // Calculate badge count (number of requested items)
  const checklistBadgeCount = useMemo(() => {
    if (!checklistItems || checklistItems.length === 0) return 0;
    return checklistItems.filter((item) => item.status === 'Requested').length;
  }, [checklistItems]);

  // Extract application ID from URL if present
  const menuOptions = useMemo(() => {
    const baseOptions = [];

    // Show "My Checklist" for family users with a submitted application
    if (isFamily && submittedApplication?.id) {
      baseOptions.push({
        label: showFamilyApplicationChecklist ? (
          <div className="flex items-center gap-2">
            My checklist
            <Badge
              count={checklistBadgeCount}
              style={{
                backgroundColor: '#fcedda',
                boxShadow: '0 0 0 1px #e0ceb6',
                color: '#8d3c23',
                fontWeight: 500,
              }}
            />
          </div>
        ) : (
          'What happens next'
        ),
        icon: () => <ListChecked />,
        to: `/family/applications/${submittedApplication.id}/submitted`,
        onClick: () => {
          history.push(`/family/applications/${submittedApplication.id}/submitted`);
        },
        match: [`/family/applications/${submittedApplication.id}/submitted`],
      });
    }

    // Show "My Application" for family users with a submitted application
    if (isFamily && submittedApplication?.id) {
      baseOptions.push({
        label: 'My application',
        icon: () => <Document />,
        to: `/family/applications/${submittedApplication.id}/start`,
        onClick: () => {
          history.push(`/family/applications/${submittedApplication.id}/start`);
        },
        match: [/^\/family\/applications\/\d+/],
      });
    }

    // If no submitted application exists, show "Applications" link
    if (!isFamily || !submittedApplication) {
      baseOptions.push({
        label: 'Applications',
        icon: () => <ListChecked />,
        to: '/family/applications',
        onClick: () => {
          history.push('/family/applications');
        },
        match: ['/family/applications'],
      });
    }

    return baseOptions;
  }, [history, isFamily, submittedApplication, showFamilyApplicationChecklist, checklistBadgeCount]);

  const _logout = useCallback(async () => {
    dispatch(logout.pending());
  }, [dispatch]);

  return (
    <Layout
      {...props}
      menuOptions={menuOptions}
      className={classNames('h-full', styles.family)}
      headerIcons={
        <>
          {checklistBadgeCount > 0 &&
            history.location.pathname !== `/family/applications/${submittedApplication?.id}/submitted` && (
              <Button
                type="default"
                className="!hidden md:!flex items-center gap-2"
                style={{
                  backgroundColor: '#fcedda',
                  borderColor: '#e0ceb6',
                  color: '#8d3c23',
                  fontWeight: 500,
                }}
                onClick={() => {
                  history.push(`/family/applications/${submittedApplication.id}/submitted`);
                }}
              >
                Action needed ({checklistBadgeCount})
              </Button>
            )}

          <Dropdown
            menu={{
              items: [
                {
                  key: 'applications',
                  label: 'Applications',
                  icon: <ListChecked />,
                  onClick: () => {
                    history.push('/family/applications');
                  },
                },
                {
                  key: 'my-account',
                  label: 'My account',
                  icon: <User />,
                  onClick: () => {
                    history.push('/account');
                  },
                },
                {
                  key: 'divider',
                  type: 'divider',
                },
                {
                  key: 'logout',
                  label: 'Logout',
                  icon: <Logout />,
                  danger: true,
                  onClick: _logout,
                },
              ],
            }}
            placement="bottomRight"
            trigger={['click']}
          >
            <Button
              type="text"
              className="border-white border-opacity-20 flex items-center gap-2"
              style={{
                color: '#fff',
              }}
            >
              {getFullName(currentUser)}
              <ChevronDown className="mb-[-1px]" />
            </Button>
          </Dropdown>
        </>
      }
      postHeader={
        checklistBadgeCount > 0 &&
        history.location.pathname !== `/family/applications/${submittedApplication?.id}/submitted` && (
          <div
            className="md:hidden flex items-center justify-between px-4 py-3 cursor-pointer"
            style={{
              backgroundColor: '#fcedda',
              borderBottom: '1px solid #e0ceb6',
            }}
            onClick={() => {
              history.push(`/family/applications/${submittedApplication.id}/submitted`);
            }}
          >
            <span
              style={{
                color: '#8d3c23',
                fontWeight: 500,
                fontSize: '14px',
              }}
            >
              Action needed ({checklistBadgeCount})
            </span>

            <ChevronDown
              style={{
                color: '#8d3c23',
                transform: 'rotate(-90deg)',
              }}
              size={20}
            />
          </div>
        )
      }
    >
      <SnapshotContext.Provider value={{ snapshotSelector: () => null }}>
        <React.Suspense fallback={<LoadingPage />}>
          <Switch>
            <ProtectedRoute path="/account" message="Navigated to My account">
              <MyAccount />
            </ProtectedRoute>
            <ProtectedRoute exact path="/family" message="Navigated to Family Applications Page">
              <Redirect to="/family/applications" />
            </ProtectedRoute>
            <ProtectedRoute exact path="/family/applications" message="Navigated to Family Applications List Page">
              <FamilyApplications householdId={householdId} />
            </ProtectedRoute>

            <ProtectedRoute exact path="/family/new-application" message="Navigated to New Application Page">
              <NewApplication />
            </ProtectedRoute>

            <ProtectedRoute exact path="/family/applications/:id" message="Navigated to Family Application Page">
              <AddApplication2 />
            </ProtectedRoute>
            <ProtectedRoute exact path="/family/applications/:id/:step" message="Navigated to Family Application Page">
              <AddApplication2 />
            </ProtectedRoute>
            <Redirect to="/family" />
          </Switch>
        </React.Suspense>
      </SnapshotContext.Provider>
    </Layout>
  );
}
